<template>
  <div class="page-wrapper-table">
    <div class="page-wrapper-table-header">
      <el-select
        class="select-primary pagination-select"
        v-model="pagination.perPage"
        :placeholder="$t('COMMON.PER_PAGE')"
      >
        <el-option
          class="select-primary"
          v-for="item in pagination.perPageOptions"
          :key="item"
          :label="item"
          :value="item"
        >
        </el-option>
      </el-select>

      <organization-selector
        v-if="
          !filterOrganization &&
          $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
        "
        @organizationChanged="
          (organizationId) => {
            s;
            selectedOrganization = organizationId;
            selectedLocations = [];
          }
        "
      />

      <base-input
        v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOCATIONS)"
        :placeholder="`${$t('COMMON.LOCATIONS')}`"
      >
        <locations-selector
          :locations="selectedLocations"
          :organization="selectedOrganization"
          @locationsChanged="(locations) => (selectedLocations = locations)"
        />
      </base-input>

      <base-input :placeholder="$t('COMMON.START_AT')">
        <flat-picker
          :placeholder="$t('COMMON.START_AT')"
          :config="{
            allowInput: true,
            enableTime: false,
            type: 'date',
            locale: $flatPickrLocale(),
            disable: [
              function (date) {
                return date.getDay() === 0;
              },
            ],
          }"
          class="form-control datepicker"
          v-model="startAt"
        >
        </flat-picker>
      </base-input>

      <base-input :placeholder="$t('COMMON.END_AT')">
        <flat-picker
          :placeholder="$t('COMMON.END_AT')"
          :config="{
            allowInput: true,
            enableTime: false,
            type: 'date',
            minDate: startAt,
            locale: $flatPickrLocale(),
            disable: [
              function (date) {
                return date.getDay() === 0;
              },
            ],
          }"
          class="form-control datepicker"
          v-model="endAt"
        >
        </flat-picker>
      </base-input>

      <base-input
        v-model="query"
        type="search"
        prepend-icon="fas fa-search"
        :placeholder="$t('COMMON.SEARCH')"
        clearable
      />

      <base-button
        class="btn base-button notification bg-dark btn-icon btn-fab btn-info btn-sm mb-4"
        icon
        size="sm"
        @click="resetFilters"
      >
        <span class="btn-inner--icon">
          <i class="fa fa-undo"></i>
        </span>
      </base-button>
    </div>
    <div class="page-wrapper-table-body">
      <div class="page-wrapper-table-body-inner">
        <el-table
          class="align-items-center table-flush"
          header-row-class-name="thead-light"
          :data="!loading ? auctions : []"
          @sort-change="sortChange"
        >
          <div class="table-loading" slot="empty" v-if="loading">
            <img src="/img/loading.gif" style="height: 100px; width: 100px" />
          </div>
          <el-table-column
            :label="$t('COMMON.NAME')"
            prop="name"
            sortable="custom"
            min-width="220"
          >
            <template v-slot="{ row }">
              <span>
                {{ `${row.name}` }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('AUCTIONS.OBJECT_TYPE')"
            prop="object_type"
            sortable="custom"
            min-width="220"
          >
            <template v-slot="{ row }">
              <span>
                {{ $t(`AUCTIONS.OBJECT_TYPE_${row.object_type}`) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('AUCTIONS.AUCTION_TYPE')"
            prop="auction_type"
            sortable="custom"
            min-width="220"
          >
            <template v-slot="{ row }">
              <span>
                {{ $t(`AUCTIONS.AUCTION_TYPE_${row.auction_type}`) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('AUCTIONS.TOTAL_PROPERTIES')"
            prop="total_properties"
            min-width="220"
          />

          <el-table-column
            :label="$t('AUCTIONS.TOTAL_PROPERTIES_CONFIRMED')"
            prop="total_properties_confirmed"
            min-width="220"
          />

          <el-table-column
            :label="$t('AUCTIONS.LISTINGS_REGISTRATIONS_OPEN_AT')"
            min-width="220"
          >
            <template v-slot="{ row }">
              <span v-if="row.listings_registrations_open_at">
                {{ $formatDate(row.listings_registrations_open_at) }}
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('AUCTIONS.LISTINGS_REGISTRATIONS_CLOSE_AT')"
            min-width="220"
          >
            <template v-slot="{ row }">
              <span v-if="row.listings_registrations_close_at">
                {{ $formatDate(row.listings_registrations_close_at) }}
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.START_AT')"
            prop="start_at"
            sortable="custom"
            min-width="220"
          >
            <template v-slot="{ row }">
              {{ $formatDate(row.start_at) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.END_AT')"
            prop="end_at"
            sortable="custom"
            min-width="220"
          >
            <template v-slot="{ row }">
              {{ $formatDate(row.end_at) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.CREATED_AT')"
            prop="created_at"
            sortable="custom"
            min-width="220"
          >
            <template v-slot="{ row }">
              {{ $formatDate(row.created_at) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.UPDATED_AT')"
            prop="created_at"
            sortable="custom"
            min-width="220"
          >
            <template v-slot="{ row }">
              {{ $formatDate(row.updated_at) }}
            </template>
          </el-table-column>

          <el-table-column fixed="right" min-width="120">
            <div slot-scope="{ row }" class="table-actions">
              <el-tooltip
                :content="$t('COMMON.VIEW')"
                placement="top"
                v-if="$currentUserCan($permissions.PERM_VIEW_MODULE_AUCTIONS)"
              >
                <a
                  type="text"
                  @click="viewAuction(row)"
                  class="table-action"
                  data-toggle="tooltip"
                  style="cursor: pointer"
                >
                  <i class="fal fa-expand-alt"></i>
                </a>
              </el-tooltip>

              <el-tooltip
                :content="$t('COMMON.EDIT')"
                placement="top"
                v-if="$currentUserCan($permissions.PERM_EDIT_AUCTIONS)"
              >
                <a
                  type="text"
                  @click="editAuction(row)"
                  class="table-action"
                  data-toggle="tooltip"
                  style="cursor: pointer"
                >
                  <i class="fal fa-edit"></i>
                </a>
              </el-tooltip>

              <el-tooltip
                content="Delete"
                placement="top"
                v-if="$currentUserCan($permissions.PERM_DELETE_AUCTIONS)"
              >
                <a
                  type="text"
                  @click="deleteAuction(row)"
                  class="table-action table-action-delete"
                  data-toggle="tooltip"
                  style="cursor: pointer"
                >
                  <i class="fal fa-trash-alt"></i>
                </a>
              </el-tooltip>
            </div>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <div slot="footer" class="page-wrapper-table-footer">
      <p class="card-category">
        {{
          $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
            from: total ? from + 1 : 0,
            to: to,
            of: total,
          })
        }}

        <span v-if="selectedRows.length">
          &nbsp; &nbsp;
          {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
        </span>
      </p>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Option,
  Select,
  Table,
  TableColumn,
  Tooltip,
} from "element-ui";
import "sweetalert2/dist/sweetalert2.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import LocationsSelector from "@/components/LocationsSelector.vue";
import { PROPERTY_STATUS } from "@/constants/properties";
import defaultAuction from "../defaultAuction";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "auction-list-table",

  components: {
    BasePagination,
    OrganizationSelector,
    LocationsSelector,
    flatPicker,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  props: {
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
  },

  data() {
    const auctionData = this.$fillUserOrganizationData(defaultAuction);
    return {
      query: null,
      startAt: null,
      endAt: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      PROPERTY_STATUS,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      auctions: [],
      loading: true,
      selectedOrganization: null,
      selectedLocations: auctionData.allowedLocations,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    filterOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedLocations: {
      handler: "getListDebounced",
      immediate: true,
    },
    endAt: {
      handler: "getListDebounced",
      immediate: true,
    },
    startAt: {
      handler: "getListDebounced",
      immediate: true,
    },
  },

  methods: {
    resetFilters() {
      this.selectedLocations = null;
      this.endAt = null;
      this.startAt = null;
      this.selectedOrganization = null;
      this.query = null;
    },
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      try {
        this.loading = true;
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: this.query } : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "organization,allowedLocations,managers",
          "fields[auctions]": "name,auction_type,auction_type,object_type,total_properties,total_properties_confirmed,start_at,end_at,listings_registrations_open_at,listings_registrations_close_at",
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.selectedOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.selectedOrganization,
            },
          };
        }
        if (this.startAt) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              start_at: this.startAt,
            },
          };
        }

        if (this.endAt) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              end_at: this.endAt,
            },
          };
        }
        if (this.selectedLocations) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              allowedLocations: this.selectedLocations.map((loc) => loc.id),
            },
          };
        }

        await this.$store.dispatch("auctions/list", params);
        this.auctions = this.$store.getters["auctions/list"];
        this.total = this.$store.getters["auctions/listTotal"];
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    deleteAuction(auction) {
      this.$emit("onDeleteAuction", auction);
    },

    viewAuction(auction) {
      this.$emit("onViewAuction", auction);
    },

    editAuction(auction) {
      this.$emit("onEditAuction", auction);
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
